import SearchIcon from '@mui/icons-material/Search';
import {Autocomplete, InputAdornment, Paper, TextField} from '@mui/material';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual.js';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import Option from './Option.jsx';
import {
  latLonFromString,
  suggestLatLon,
  suggestMGRS,
  testMGRS,
} from './util.js';

const identity = () => {};
/**
 * @param {string} inputValue - The input value
 * @param {Function} onClear - callback fired when the input is cleared
 * @param {Function} onError - callback fired when there is an error fetching from provided Promises
 * @param {Function} onSelectOption - callback fired when an option is selected from the results list
 * @param {array[Promises]} [suggestionsPromises] - optional array of promises
 * @param {Function(props, option)} [renderOption] - optional override for rendering results
 */
const LocationSearchAutoComplete = ({
  inputValue = '',
  onClear = identity,
  onError = identity,
  onSelectOption = identity,
  suggestionsPromises = [],
  renderOption = null,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setQuery(inputValue);
  }, [inputValue]);

  const search = useCallback(
    (query, callback) => {
      if (latLonFromString(query)) {
        callback(suggestLatLon(query));
      } else if (testMGRS(query) !== -1) {
        callback(suggestMGRS(query));
      } else {
        let requests = [];

        if (suggestionsPromises) {
          requests = suggestionsPromises
            .map(suggestionFunc => suggestionFunc(query))
            .concat(requests);
        }
        Promise.all(requests)
          .then(allResults => {
            const results = [].concat.apply([], allResults);
            callback(results);
          })
          .catch(error => {
            setLoading(false);
            if (onError) {
              onError(error);
            }
          });
      }
    },
    [onError, suggestionsPromises],
  );

  const debouncedSearch = useMemo(() => {
    return debounce(query => {
      setLoading(true);
      search(query, results => {
        if (results) {
          setOptions(results);
        }
        setLoading(false);
      });
    }, 700);
  }, [search]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = useCallback(
    (event, newValue, reason) => {
      //trim leading spaces only here to allow spaces in between words
      setQuery(newValue.trimStart());
      if (reason === 'clear') {
        setOptions([]);
        if (onClear) {
          onClear();
        }
      } else if (reason === 'input') {
        //submit trimmed on both ends for queries
        const trimmedValue = newValue.trim();

        if (trimmedValue && trimmedValue.length >= 3) {
          debouncedSearch(trimmedValue);
        }
      }
    },
    [debouncedSearch, onClear],
  );

  const onSelectResult = async (_, result) => {
    if (result) {
      if (onSelectOption) {
        onSelectOption(result);
      }
    }
  };

  return (
    <Paper elevation={2}>
      <Autocomplete
        freeSolo
        fullWidth
        autoHighlight
        autoComplete
        includeInputInList
        noOptionsText="No matching locations found"
        loading={loading}
        isOptionEqualToValue={(option, value) => {
          return isEqual(option, value);
        }}
        getOptionLabel={option => option.label || option.title}
        filterOptions={x => x}
        options={options}
        inputValue={query}
        onChange={onSelectResult}
        onInputChange={handleInputChange}
        renderOption={({id, ...props}, option) => {
          if (renderOption) {
            return renderOption({id, ...props}, option);
          } else {
            return (
              <Option
                {...props}
                key={id}
                title={option.title}
                description={option.description}
              />
            );
          }
        }}
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            margin="none"
            placeholder="Search for a location"
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
        sx={{
          '& .MuiAutocomplete-clearIndicatorDirty, & .MuiAutocomplete-clearIndicator':
            {
              visibility: 'visible',
            },
        }}
      />
    </Paper>
  );
};

export default LocationSearchAutoComplete;
